import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { formatDate } from "../../../utils/helper";
import './Broadcasts.css';

/**
 * Delivery report for one broadcast: what was sent, to whom, and what failed.
 *
 * Per-user rows come from the recipient queue, so a failure shows the provider's
 * own error rather than a generic "not delivered".
 */

const STATUS_BADGE = {
    scheduled: 'badge-light-warning',
    processing: 'badge-light-info',
    completed: 'badge-light-success',
    cancelled: 'badge-light-dark',
    failed: 'badge-light-danger',
};

const DELIVERY_BADGE = {
    pending: 'badge-light-secondary',
    sent: 'badge-light-success',
    failed: 'badge-light-danger',
    skipped: 'badge-light-warning',
};

const CHANNEL_LABEL = { push: 'Push', email: 'Email', both: 'Push + Email', sms: 'SMS' };

const Details = () => {
    const { id } = useParams();

    const [isLoader, setIsLoader] = useState(false);
    const [broadcast, setBroadcast] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({ delivery_status: '', page: 1 });

    useEffect(() => {
        getDetails();
    }, [id, apiData.page, apiData.delivery_status]); // Dependency array for re-fetching data

    // Keep the report live while delivery is still running.
    useEffect(() => {
        if (!broadcast || !['processing', 'scheduled'].includes(broadcast.status)) return;
        const interval = setInterval(getDetails, 10000);
        return () => clearInterval(interval);
    }, [broadcast]); // eslint-disable-line react-hooks/exhaustive-deps

    const getDetails = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: `admin/broadcasts/${id}`,
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setBroadcast(data.data);
                setRecipients(data.recipients || []);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const stats = broadcast?.stats || {};
    const total = stats.total || 0;
    const done = (stats.sent || 0) + (stats.failed || 0) + (stats.skipped || 0);
    const progress = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Notification Details</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/broadcasts" className="text-muted text-hover-primary">Send Notifications</Link>
                            </li>
                        </ul>
                    </div>
                    <Link to="/admin/broadcasts" className="btn btn-sm btn-light">
                        <i className="bi bi-arrow-left me-2"></i>Back
                    </Link>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">

                    {broadcast && (
                        <>
                            <div className="card mb-5">
                                <div className="card-body py-6">
                                    <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                                        <div className="me-4">
                                            <h3 className="text-dark fw-bold mb-2">{broadcast.title}</h3>
                                            <p className="bc-message mb-0">{broadcast.body}</p>
                                        </div>
                                        <span className={`badge ${STATUS_BADGE[broadcast.status] || 'badge-light-dark'} text-capitalize fs-7`}>
                                            {broadcast.status === 'processing' ? 'Sending' : broadcast.status}
                                        </span>
                                    </div>

                                    <div className="bc-progress mb-2">
                                        <div className="bc-progress-bar" style={{ width: `${progress}%` }}></div>
                                    </div>
                                    <div className="text-muted fs-7 mb-5">
                                        {total ? `${done} of ${total} recipients processed (${progress}%)` : 'Not started yet'}
                                    </div>

                                    <div className="row g-4">
                                        <div className="col-md-3 col-6">
                                            <div className="text-muted fs-8 text-uppercase">Channel</div>
                                            <div className="text-dark fw-bold">{CHANNEL_LABEL[broadcast.channel] || broadcast.channel}</div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="text-muted fs-8 text-uppercase">Audience</div>
                                            <div className="text-dark fw-bold">
                                                {broadcast.audience === 'all' ? 'All users' : `${broadcast.user_ids?.length || 0} selected`}
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="text-muted fs-8 text-uppercase">Scheduled</div>
                                            <div className="text-dark fw-bold">{formatDate(broadcast.scheduled_at, 'MMM D, YYYY hh:mm A')}</div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="text-muted fs-8 text-uppercase">Created by</div>
                                            <div className="text-dark fw-bold">{broadcast.created_by?.name || '—'}</div>
                                        </div>
                                    </div>

                                    {broadcast.last_error && (
                                        <div className="alert alert-danger mt-5 mb-0 py-3">
                                            <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                            {broadcast.last_error}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row g-4 mb-5">
                                {[
                                    { label: 'Sent', value: stats.sent || 0, icon: 'bi bi-check-circle-fill', color: 'text-success' },
                                    { label: 'Failed', value: stats.failed || 0, icon: 'bi bi-x-circle-fill', color: 'text-danger' },
                                    { label: 'Skipped', value: stats.skipped || 0, icon: 'bi bi-dash-circle-fill', color: 'text-warning' },
                                    { label: 'Push Sent', value: stats.push_sent || 0, icon: 'bi bi-phone-fill', color: 'text-primary' },
                                    { label: 'Emails Sent', value: stats.email_sent || 0, icon: 'bi bi-envelope-fill', color: 'text-info' },
                                    // Only SMS broadcasts can have texts, so the tile would just read 0 elsewhere.
                                    ...(broadcast.channel === 'sms'
                                        ? [{ label: 'SMS Sent', value: stats.sms_sent || 0, icon: 'bi bi-chat-text-fill', color: 'text-success' }]
                                        : []),
                                ].map((tile) => (
                                    <div className="col-md" key={tile.label}>
                                        <div className="card h-100">
                                            <div className="card-body d-flex align-items-center py-4">
                                                <i className={`${tile.icon} fs-2x ${tile.color} me-4`}></i>
                                                <div>
                                                    <div className="fs-3 fw-bold text-dark">{tile.value}</div>
                                                    <div className="fs-8 text-gray-500">{tile.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="d-flex flex-stack w-100">
                                <h3 className="fw-bold text-dark my-0">Recipients</h3>
                                <select
                                    className="form-select form-select-solid w-200px"
                                    value={apiData.delivery_status}
                                    onChange={(e) => setApiData({ delivery_status: e.target.value, page: 1 })}
                                >
                                    <option value="">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="sent">Sent</option>
                                    <option value="failed">Failed</option>
                                    <option value="skipped">Skipped</option>
                                </select>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>{("User")}</th>
                                            <th>{("Push")}</th>
                                            <th>{("Email")}</th>
                                            <th>{("SMS")}</th>
                                            <th>{("Result")}</th>
                                            <th>{("Processed")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {recipients.length > 0 ? recipients.map((row, index) => (
                                            <tr key={row._id || index}>
                                                <td>
                                                    <div className="text-dark fw-bold">{row.user_id?.name || 'Deleted user'}</div>
                                                    <div className="text-muted fs-7">{row.user_id?.email}</div>
                                                    {broadcast.channel === 'sms' && <div className="text-muted fs-7">{row.user_id?.phone}</div>}
                                                </td>
                                                <td><span className={`badge ${DELIVERY_BADGE[row.push_status]} text-capitalize`}>{row.push_status}</span></td>
                                                <td><span className={`badge ${DELIVERY_BADGE[row.email_status]} text-capitalize`}>{row.email_status}</span></td>
                                                <td><span className={`badge ${DELIVERY_BADGE[row.sms_status || 'skipped']} text-capitalize`}>{row.sms_status || 'skipped'}</span></td>
                                                <td>
                                                    <span className={`badge ${DELIVERY_BADGE[row.status]} text-capitalize`}>{row.status}</span>
                                                    {row.error && <div className="text-danger fs-8 mt-1" style={{ maxWidth: 320 }}>{row.error}</div>}
                                                </td>
                                                <td>{row.processed_at ? formatDate(row.processed_at, 'MMM D, hh:mm A') : <span className="text-muted">—</span>}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6" className="text-dark text-center mt-2">No data found!!</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {isLoader && (
                                    <Loader text="Loading" size="30" className="text-center" />
                                )}

                                {pagination.total_pages > 1 && (
                                    <Paginator
                                        page={pagination.current_page}
                                        rows={pagination.per_page}
                                        totalRecords={pagination.total_records}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Details;
